import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Profile, Patient } from '@/types/database';

// Auth Store
interface AuthState {
  user: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

// Supabase owns session persistence; a cached profile must never grant access.
export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () => set({ user: null, isAuthenticated: false }),
}));

// Patient Store
interface PatientState {
  patients: Patient[];
  selectedPatient: Patient | null;
  isLoading: boolean;
  setPatients: (patients: Patient[]) => void;
  addPatient: (patient: Patient) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  removePatient: (id: string) => void;
  selectPatient: (patient: Patient | null) => void;
  setLoading: (loading: boolean) => void;
}

export const usePatientStore = create<PatientState>()((set) => ({
  patients: [],
  selectedPatient: null,
  isLoading: false,
  setPatients: (patients) => set({ patients }),
  addPatient: (patient) => set((state) => ({ patients: [...state.patients, patient] })),
  updatePatient: (id, updates) =>
    set((state) => ({
      patients: state.patients.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      selectedPatient:
        state.selectedPatient?.id === id
          ? { ...state.selectedPatient, ...updates }
          : state.selectedPatient,
    })),
  removePatient: (id) =>
    set((state) => ({
      patients: state.patients.filter((p) => p.id !== id),
      selectedPatient: state.selectedPatient?.id === id ? null : state.selectedPatient,
    })),
  selectPatient: (patient) => set({ selectedPatient: patient }),
  setLoading: (isLoading) => set({ isLoading }),
}));

// UI Store
interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: 'light',
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'nutry-ui',
    }
  )
);

// Diet Editor Store
interface DietEditorState {
  isDirty: boolean;
  currentDay: number;
  activeMealIndex: number;
  setDirty: (dirty: boolean) => void;
  setCurrentDay: (day: number) => void;
  setActiveMeal: (index: number) => void;
  hasChanges: boolean;
  setHasChanges: (hasChanges: boolean) => void;
}

export const useDietEditorStore = create<DietEditorState>()((set) => ({
  isDirty: false,
  currentDay: 1,
  activeMealIndex: 0,
  hasChanges: false,
  setDirty: (isDirty) => set({ isDirty }),
  setCurrentDay: (currentDay) => set({ currentDay }),
  setActiveMeal: (activeMealIndex) => set({ activeMealIndex }),
  setHasChanges: (hasChanges) => set({ hasChanges, isDirty: hasChanges }),
}));
